/**
 * Catalog API endpoints
 *
 * For static export, these endpoints call the backend API directly
 * using the /api/v1/ prefix with namespace/name pattern.
 */
import {
  Catalog,
  CatalogCreateResponse,
  CatalogFormData,
  CatalogListResponse,
  CatalogRefreshRequest,
  CatalogRefreshResponse,
  CatalogStatus,
} from '@/features/catalogs/types/catalog';
import apiClient, { QueryParams } from './client';
import { ApiListParams } from './types';

// Base URL for catalogs API - single source of truth
export const CATALOGS_API_BASE = '/api/v1/catalogs';

export interface CatalogListParams extends ApiListParams {
  namespace?: string;
}

// Backend may return status with inconsistent casing across catalogs ("Ready"
// vs "ready"). Normalize to PascalCase so downstream comparisons against the
// CatalogStatus union ("Ready" | "Pending" | …) are robust either way.
export function normalizeStatus(status: string | undefined): string {
  if (!status) return status ?? '';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

export function normalizeCatalog(catalog: Catalog): Catalog {
  if (!catalog?.status) return catalog;
  return {
    ...catalog,
    status: normalizeStatus(catalog.status) as CatalogStatus,
  };
}

export const catalogsApi = {
  /**
   * List all catalogs with optional filtering and pagination
   */
  list: async (params?: CatalogListParams): Promise<CatalogListResponse> => {
    const response = await apiClient.get<CatalogListResponse>(CATALOGS_API_BASE, {
      params: params as QueryParams,
    });
    return {
      ...response,
      items: Array.isArray(response.items) ? response.items.map(normalizeCatalog) : response.items,
    };
  },

  /**
   * Get a single catalog by name
   */
  get: async (name: string): Promise<Catalog> => {
    const response = await apiClient.get<Catalog>(
      `${CATALOGS_API_BASE}/${encodeURIComponent(name)}`
    );
    return normalizeCatalog(response);
  },

  /**
   * Create a new catalog
   */
  create: (data: CatalogFormData): Promise<CatalogCreateResponse> => {
    return apiClient.post(CATALOGS_API_BASE, data);
  },

  /**
   * Update an existing catalog
   */
  update: (name: string, data: Partial<CatalogFormData>): Promise<CatalogCreateResponse> => {
    return apiClient.put(`${CATALOGS_API_BASE}/${encodeURIComponent(name)}`, data);
  },

  /**
   * Replace a catalog's filtering spec — which schemas it exposes.
   *
   * Separate from update() because the backend gates this on
   * catalogs:schema_update rather than catalogs:update, so a data steward can
   * reshape exposure without access to connection credentials.
   */
  updateFilters: (name: string, data: { schemas: string[] }): Promise<CatalogCreateResponse> => {
    return apiClient.put(`${CATALOGS_API_BASE}/${encodeURIComponent(name)}/filters`, data);
  },

  /**
   * Delete a catalog
   */
  delete: (name: string): Promise<CatalogCreateResponse> => {
    return apiClient.delete(`${CATALOGS_API_BASE}/${encodeURIComponent(name)}`);
  },

  /**
   * Trigger catalog refresh
   */
  refresh: (name: string, params: CatalogRefreshRequest): Promise<CatalogRefreshResponse> => {
    return apiClient.post(`${CATALOGS_API_BASE}/${encodeURIComponent(name)}/refresh`, params);
  },

  /**
   * Test catalog connection and get schemas (for create mode)
   */
  testConnection: (
    data: CatalogFormData
  ): Promise<{ success: boolean; message?: string; schemas?: string[]; databases?: string[] }> => {
    return apiClient.post(`${CATALOGS_API_BASE}/schemas`, data);
  },

  /**
   * Get schemas by catalog name (for edit mode)
   * Uses the catalog's existing configuration stored on the server
   */
  getSchemasByName: async (
    name: string
  ): Promise<{ success: boolean; schemas?: string[]; databases?: string[]; message?: string }> => {
    try {
      const response = await apiClient.get<{ schemas?: string[]; databases?: string[] } | string[]>(
        `${CATALOGS_API_BASE}/${encodeURIComponent(name)}/schemas`
      );

      // Handle both array response and object response
      if (Array.isArray(response)) {
        return { success: true, schemas: response };
      }
      return {
        success: true,
        schemas: response.schemas,
        databases: response.databases,
      };
    } catch (error) {
      // Return error in consistent format
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to fetch schemas',
      };
    }
  },

  /**
   * Get schemas for a catalog from storage service
   */
  getSchemas: async (catalogName: string, userName: string): Promise<string[]> => {
    const response = await apiClient.get<{ databases?: string[]; schemas?: string[] } | string[]>(
      `/api/v1/storage/${encodeURIComponent(catalogName)}/schemas`,
      {
        headers: { 'X-User-Name': userName },
      }
    );

    // Handle both array response and object response with databases/schemas field
    if (Array.isArray(response)) {
      return response;
    }
    return response.databases || response.schemas || [];
  },

  /**
   * Get tables for a schema in a catalog from storage service
   */
  getTables: async (
    catalogName: string,
    schemaName: string,
    userName: string
  ): Promise<string[]> => {
    const response = await apiClient.get<{ objects?: Array<{ dbObjectName: string }> } | string[]>(
      `/api/v1/storage/${encodeURIComponent(catalogName)}/${encodeURIComponent(schemaName)}/tables`,
      {
        headers: { 'X-User-Name': userName },
      }
    );

    // Handle both array response and object response with objects field
    if (Array.isArray(response)) {
      return response;
    }
    return (response.objects || []).map((obj) => obj.dbObjectName);
  },

  /**
   * Browse catalog metadata without governance filtering.
   * Used by the governance page to see all available resources when creating policies.
   *
   * POST /api/v1/storage/browse
   *
   * - catalogName only → lists schemas
   * - catalogName + schemaName → lists tables
   * - catalogName + schemaName + tableName → lists columns
   */
  browseSchemas: async (catalogName: string): Promise<string[]> => {
    const response = await apiClient.post<{
      type: string;
      catalogName: string;
      schemas?: string[];
    }>('/api/v1/storage/browse', { catalogName });

    return response.schemas ?? [];
  },

  browseTables: async (catalogName: string, schemaName: string): Promise<string[]> => {
    const response = await apiClient.post<{
      type: string;
      catalogName: string;
      schemaName: string;
      tables?: Array<{ dbObjectName: string; dbObjectType: string }>;
    }>('/api/v1/storage/browse', { catalogName, schemaName });

    return (response.tables ?? []).map((t) => t.dbObjectName);
  },

  /**
   * Get diagnostics for a catalog (when operation status is partial_success)
   * Returns the response as-is (could be text or JSON depending on API response)
   */
  getDiagnostics: (name: string): Promise<unknown> => {
    // Use apiClient which handles authentication and proper API routing
    return apiClient.get(`${CATALOGS_API_BASE}/${encodeURIComponent(name)}/diagnostics`);
  },
};

// Helper function to construct catalog ID for UI (now just returns the name)
export function getCatalogId(name: string): string {
  return name;
}

// Helper function to parse catalog ID (now just returns the name)
export function parseCatalogId(id: string): { name: string } {
  return { name: id };
}

export default catalogsApi;
